import React from "react";

import {
    CompactSplitBlock as ItemValue,
    CompactSplitBlockWrapper as Value,
} from "@reactivated";

import {
    HeroLayoutProps,
    HeroLayoutSettings,
} from "@thelabnyc/thelabui/src/components/Flexihero/HeroLayout.interfaces";
import { HeroSet } from "@thelabnyc/thelabui/src/components/Flexihero/HeroSet";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { Themed } from "../../styles/themes";
import { MediaChooserBlock } from "../MediaChooserBlock";
import { RichTextStreamBlock } from "../RichTextStreamBlock";
import { SpacerBlock } from "../SpacerBlock";

import styles from "./index.module.scss";

type Layout = ItemValue["layout"];

const isContentRight = (layout: Layout) =>
    layout === "right" || layout === null;

const desktopSettings = (layout: Layout): HeroLayoutSettings => {
    const base: HeroLayoutSettings = {
        heroDirection: "row",
        heroPlacement: "container",
        mediaPlacement: "inline",
        mediaAspectRatio: "1:1",
        contentAlignPrimary: "middle",
        contentAlignSecondary: "left/top",
        textAlignment: "left",
        contentWidth: 6,
        mediaWidth: 6,
    };
    return isContentRight(layout)
        ? { ...base, contentAlignSecondary: "right/bottom" }
        : base;
};

const mobileSettings: HeroLayoutSettings = {
    heroDirection: "column",
    heroPlacement: "container",
    mediaPlacement: "inline",
    mediaAspectRatio: "none",
    contentAlignPrimary: "left/top",
    contentAlignSecondary: "right/bottom",
    textAlignment: "left",
    contentWidth: 12,
};

const CompactSplit = ({ value }: { value: ItemValue }) => {
    if (!value.media) return null;

    const media = (
        <MediaChooserBlock
            value={value.media}
            imageProps={{ sizes: styles.sizes }}
        />
    );
    const content = (breakpoint: "desktop" | "mobile") =>
        value.content ? (
            <RichTextStreamBlock
                value={{
                    ...value.content,
                    container_width: "none",
                    alignment: breakpoint === "mobile" ? "center" : "none",
                }}
            />
        ) : null;

    const desktopStyles: HeroLayoutProps["styles"] = {
        contentClassName: isContentRight(value.layout)
            ? styles.contentRight
            : styles.contentLeft,
        innerClassName: styles.inset,
    };

    return (
        <HeroSet
            layouts={{
                desktop: {
                    content: content("desktop"),
                    media,
                    settings: desktopSettings(value.layout),
                    styles: desktopStyles,
                },
                mobile: {
                    content: content("mobile"),
                    media,
                    settings: mobileSettings,
                },
            }}
        />
    );
};

const components = {
    spacer: SpacerBlock,
    split_block: CompactSplit,
};

export const CompactSplitBlockWrapper = ({ value }: { value: Value }) => {
    if (!value.items) return null;

    return (
        <Themed theme={value.theme} tag="section">
            <StreamField components={components} value={value.items} />
        </Themed>
    );
};
